import React, { useRef, useState } from 'react';
import { uploadCoverPhoto, getCoverPhotoUrl } from '../../lib/api';
import { Button } from '../ui/button';
import { Image as ImageIcon, Upload } from 'lucide-react';
import { toast } from 'sonner';

export const CoverPhotoBlock = ({ dossierId, hasCoverPhoto, onUpdated }) => {
    const fileRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [cacheBust, setCacheBust] = useState(() => Date.now());
    const [imgError, setImgError] = useState(false);
    const [uploadedOnce, setUploadedOnce] = useState(false);

    const hasPhoto = uploadedOnce || !!hasCoverPhoto;
    const showImage = hasPhoto && !imgError;

    const handleFile = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (!f.type.startsWith('image/')) {
            toast.error('Merci de choisir une image');
            return;
        }
        if (f.size > 5 * 1024 * 1024) {
            toast.error('Image trop volumineuse (max 5 Mo)');
            return;
        }
        setUploading(true);
        try {
            await uploadCoverPhoto(dossierId, f);
            toast.success('Photo de couverture mise à jour');
            setUploadedOnce(true);
            setImgError(false);
            setCacheBust(Date.now());
            onUpdated && onUpdated();
        } catch (err) {
            const msg = err?.response?.data?.detail || "Erreur lors de l'upload";
            toast.error(typeof msg === 'string' ? msg : "Erreur lors de l'upload");
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = '';
        }
    };

    const imgSrc = `${getCoverPhotoUrl(dossierId)}?t=${cacheBust}`;

    return (
        <div className="bg-white border border-[#E4E4E7] rounded-sm overflow-hidden" data-testid="cover-photo-block">
            <div className="relative w-full h-40 bg-[#F4F4F5]">
                {showImage ? (
                    <img
                        key={cacheBust}
                        src={imgSrc}
                        alt="Photo de couverture"
                        className="absolute inset-0 w-full h-full object-cover"
                        onError={() => setImgError(true)}
                        data-testid="cover-photo-img"
                    />
                ) : (
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-[#A1A1AA]">
                        <ImageIcon className="w-10 h-10 mb-1" />
                        <p className="text-xs">Aucune photo de couverture</p>
                    </div>
                )}
                <Button
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    size="sm"
                    className="absolute bottom-2 right-2 bg-white/95 text-[#0A0A0A] hover:bg-white rounded-sm shadow-sm"
                    data-testid="upload-cover-photo-btn"
                >
                    <Upload className="w-4 h-4 mr-1" /> {uploading ? 'Upload...' : (hasPhoto ? 'Changer la photo' : 'Photo de couverture')}
                </Button>
                <input
                    ref={fileRef}
                    type="file"
                    accept="image/*,.heic,.heif"
                    className="hidden"
                    onChange={handleFile}
                    data-testid="cover-photo-input"
                />
            </div>
        </div>
    );
};
